import { fetchFromServer, syncDependency, type DependencySyncConfig, type FetchFromServerOptions } from "./dependency-sync";
import { simplePages } from "@/services/cache";
import { simplePagesSyncKey } from "@/types/products/product-keys";
import type { ApiFetchResponse } from "@tmi/types/api/fetch";
import type { SimplePages } from "@tmi/types/api/content-models";
import type { SimplePageContent } from "@tmi/types/entities";

const PAGE_SIZE = 50;

export const config: DependencySyncConfig<SimplePages, SimplePageContent, SimplePageContent> = {
    name: 'simple-page',
    store: () => simplePages,
    syncKey: simplePagesSyncKey,
    throttle: { hours: 4 },
    pageSize: PAGE_SIZE,
    // R26 trap 2: `skip` is a PAGE INDEX on the content catalogs, not a row offset — the producer
    // asserts it (UngatedCatalogPagingTests). `page * pageSize` returns one record in every
    // pageSize and looks perfectly healthy from here.
    buildRequest: ( page: number, pageSize: number ) => ( {
        endpoint: `content/simple-page-catalog?skip=${ page }&limit=${ pageSize }`,
        verbOverride: 'GET'
    } ),
    // R26 trap 1: the content twins carry NO `totalCount`. Copying the sentient tickers
    // `isComplete` — `fetchedSoFar >= ( totalCount ?? MAX_SAFE_INTEGER )` — would never once be
    // true here, so every sweep would be permanently partial: never pruning, and re-running on
    // the 15-second partial-retry threshold forever. Completion is "the last page was short",
    // which is also the paging loop's own exit condition.
    isComplete: ( response: ApiFetchResponse<SimplePages> ) => ( response?.data?.simplePageCatalog?.simple_pages?.length ?? 0 ) < PAGE_SIZE,
    // The content twins send no ETag, and a 200 carrying `[]` from Contentful is not trustworthy
    // evidence that the catalog is empty — so no ETag alarm, and no whole-store prune.
    expectsEtag: false,
    extractPayload: ( response: ApiFetchResponse<SimplePages> ) => response?.data?.simplePageCatalog?.simple_pages,
    mapItem: ( p: SimplePageContent ): SimplePageContent => ( {
        id: p.id,
        title: p.title,
        slug: p.slug,
        body: p.body
    } )
};

/**
 * The main thread's catalog read (stores/SimplePages.ts). With `throwOnServiceUnavailable` a 503 before the server's
 * first cache fill rejects instead of answering an empty catalog, which the page read as NotFound (tmi-978). The sync
 * worker uses refreshSimplePageCache below and never sets it.
 */
export async function refreshSimplePagesFromServer( options: FetchFromServerOptions = {} ): Promise<SimplePageContent[]> {
    // Callers of this wrapper only want the records; partial-fetch handling lives in syncDependency.
    return ( await fetchFromServer( config, undefined, undefined, options ) ).items;
}

export async function refreshSimplePageCache(): Promise<boolean> {
    return syncDependency( config );
}
